import { useState } from 'react';
import { useTranslate } from 'react-polyglot';

import { UserAliasAvatar } from '@genially/ui/build/components/UserAliasAvatar/UserAliasAvatar';
import { getSavedLanguage } from '@genially/ui/build/utils/language';

import type { QuestionOpenAnswer } from '../../../../../../utils/buildQuestionAnalytics';
import { UserResultsToggle } from '../../../common/UserResultsToggle';
import {
  AnswerCard,
  AnswerText,
  Identity,
  IdentityRow,
  List,
  Name,
  SentAt,
  Wrapper,
} from './QuestionOpenAnswers.styled';

interface QuestionOpenAnswersProps {
  answers: QuestionOpenAnswer[];
}

/**
 * An open answer question has nothing to aggregate, so the texts themselves are the
 * result. The toggle only adds identity to this one list rather than opening a second,
 * nominal copy of it: it would be the same information twice in the same scroll.
 *
 * With the toggle off the whole identity row goes, avatar included — `UserAliasAvatar`
 * derives its initials and its colour from the name, so leaving it visible (or blurring
 * it) would hand the room two guessable letters per person.
 */
export const QuestionOpenAnswers = ({ answers }: QuestionOpenAnswersProps) => {
  const t = useTranslate();

  // Not lifted anywhere: the parent mounts this list with the question's id as its key, so
  // moving to another question remounts it and every question starts anonymous.
  const [areNamesVisible, setAreNamesVisible] = useState(false);

  const handleChange = () => {
    setAreNamesVisible(previous => !previous);
  };

  const language = getSavedLanguage();

  return (
    <Wrapper>
      <UserResultsToggle
        label={t('__new.play.analyticsResults.question.showNames')}
        isChecked={areNamesVisible}
        onChange={handleChange}
      />

      <List data-cy="analytics-open-answers">
        {answers.map(({ username, text, timestamp }) => {
          // Formatted by the browser in the session's language rather than by hand: a
          // hand-built dd/mm/yyyy reads as a different date in English.
          const sentAt = new Date(timestamp).toLocaleString(language);

          return (
            <AnswerCard key={`${username}-${timestamp}`}>
              <IdentityRow $isAnonymous={!areNamesVisible}>
                {areNamesVisible ? (
                  <>
                    <UserAliasAvatar name={username} size={UserAliasAvatar.Size.Small} />

                    <Identity>
                      <Name>{username}</Name>
                      <SentAt>{sentAt}</SentAt>
                    </Identity>
                  </>
                ) : (
                  <SentAt>{sentAt}</SentAt>
                )}
              </IdentityRow>

              <AnswerText>{text}</AnswerText>
            </AnswerCard>
          );
        })}
      </List>
    </Wrapper>
  );
};
